<template>
  <v-dialog
    v-model="enableRecurringAutopayModal"
    max-width="750px"
    @click:outside="handleCloseClickOutside()"
  >
    <template #activator="{ props }">
      <v-btn
        v-if="hasPermission('view ticket::cancel-payment')"
        class="mr-2 btn-bg-regal-gray"
        v-bind="props"
      >
        <v-icon>mdi-plus</v-icon>
        Enable Recurring Autopay
      </v-btn>
      <disabled-for-lack-of-role-btn
        v-else
        icon="mdi-pencil-outline"
        text="Edit"
        permission="view ticket::cancel-payment"
        :x-small="true"
        button-class="mr-2"
      />
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Enable Recurring Autopay</span>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-stepper v-model="step">
          <v-stepper-header>
            <v-stepper-item
              :complete="step > 1"
              :value="1"
              title="Autopay Information"
            />

            <v-divider />

            <v-stepper-item
              :complete="step >= 2"
              :value="2"
              title="Autopay Details"
            />

            <v-divider />

            <v-stepper-item
              :complete="step >= 3"
              :value="3"
              title="Federal Regulation"
            />

            <v-stepper-item
              :complete="step === 4"
              :value="4"
              title="EFT Document"
            />
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item :value="1">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col
                      cols="12"
                      class="mb-0"
                    >
                      <p>
                        To enable automatic payment, select a payment
                        method and agree to electronic disclosure
                      </p>

                      <v-select
                        v-model="paymentProfile"
                        :items="paymentProfiles"
                        item-value="id"
                        item-title="title"
                        label="Payment Method"
                        outlined
                        required
                        :class="{ 'is-invalid': v$.paymentProfile.$error }"
                        :error-messages="paymentProfileErrors"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :disabled="!paymentProfile"
                    color="primary"
                    :loading="loading"
                    @click="nextStep()"
                  >
                    Continue
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-window-item>

            <v-stepper-window-item :value="2">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <h2 class="talk__off-one_title">
                        <b>Representative:</b>
                      </h2>
                      <blockquote class="blockquote">
                        "I can help set up your recurring loan payment. By authorizing this,
                        you are allowing TBO Bank to electronically withdraw funds from
                        your account as agreed upon."
                      </blockquote>

                      <blockquote
                        v-if="showNextDateNotice"
                        class="blockquote error--text"
                      >
                        "If you enroll in autopay now, it will take effect starting with
                        your following payment due date. Your upcoming immediate payment
                        will still need to be made manually."
                      </blockquote>

                      <div class="talk__off-payment-details">
                        <h3>Please confirm the following details:</h3>

                        <ul>
                          <li>
                            <span> Payment Amount: ${{ recurringAutopayAmount }} </span>
                          </li>
                          <li>
                            <span> Payment Frequency: {{ paymentsFrequency }} </span>
                          </li>
                          <li>
                            <span>
                              First Payment Date:
                              {{ convertDateFormat(firstPaymentDate) }}
                            </span>
                          </li>
                          <li>
                            <span v-if="bankAccountTypeText === 'Debit Card'">
                              Debit Card Ending In: {{ accountLast4 }}
                            </span>
                            <span v-else> Bank Account Ending In: {{ accountLast4 }} </span>
                          </li>
                          <li>
                            <span>
                              Final Payment Date:
                              {{ convertDateFormat(finalPaymentDate) }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-checkbox
                        v-model="electronicDisclosureConsent"
                        :label="`Do you confirm that these details are correct?`"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    text
                    @click="step = 1"
                  >
                    Back
                  </v-btn>

                  <v-spacer />

                  <v-btn
                    :disabled="electronicDisclosureConsent === false"
                    color="primary"
                    :loading="loading"
                    @click="nextStep()"
                  >
                    <span> Continue </span>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-window-item>

            <v-stepper-window-item :value="3">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h2 class="talk__off-one_title">
                        <b>Representative:</b>
                      </h2>

                      <div class="talk__off-payment-details mt-2">
                        <h3>Under Federal Regulations, we must inform you that:</h3>

                        <ul>
                          <li>
                            <blockquote class="blockquote">
                              "You can cancel this authorization at any time by
                              notifying us at least three business days before your
                              next payment by calling us at 561-462-8527 or emailing us at
                              supportteam@tbobank.com."
                            </blockquote>
                          </li>
                          <li>
                            <blockquote class="blockquote">
                              "A written confirmation of this authorization will be
                              sent to you at {{ customerEmail }}. Please review and
                              keep it for your records."
                            </blockquote>
                          </li>
                          <li>
                            <blockquote class="blockquote">
                              "If your payment amount is different, we will notify you at
                              least 10 business days in advance."
                            </blockquote>
                          </li>
                        </ul>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-checkbox
                        v-model="agreeFederalRegulations"
                        :label="`Do you confirm that these details are correct?`"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    text
                    @click="step = 2"
                  >
                    Back
                  </v-btn>

                  <v-spacer />

                  <v-btn
                    :disabled="agreeFederalRegulations === false"
                    color="primary"
                    :loading="loading"
                    @click="nextStep()"
                  >
                    Continue
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-window-item>

            <v-stepper-window-item :value="4">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h2 class="talk__off-one_title">
                        <b>Representative:</b>
                      </h2>

                      <div class="talk__off-payment-details mt-2">
                        <p>Click on the button to sent the EFT Document and Create the Autopay</p>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    text
                    @click="step = 2"
                  >
                    Back
                  </v-btn>

                  <v-spacer />

                  <v-btn
                    :disabled="loading"
                    color="primary"
                    :loading="loading"
                    @click="enableRecurringPayment"
                  >
                    Send EFT Document and Create Autopay
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { DateTime } from 'luxon';
import DisabledForLackOfRoleBtn from '../../DisabledForLackOfRoleBtn.vue';

export default {
  name: 'EnableRecurringAutopay',
  components: {
    DisabledForLackOfRoleBtn,
  },
  props: {
    autopayType: {
      type: String,
      required: false,
      default: 'normal',
      validator: (value) => ['normal', 'settlement'].includes(value),
    },
  },
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      enableRecurringAutopayModal: false,
      loading: false,
      paymentProfile: null,
      step: 1,
      electronicDisclosureConsent: false,
      agreeFederalRegulations: false,
    };
  },
  validations() {
    return {
      paymentProfile: { required },
    };
  },
  computed: {
    ...mapState('loanProCustomer', ['paymentProfiles', 'customer']),
    ...mapState('settlement', ['disclosuresSent']),
    ...mapState('loanProLoan', ['activeLoan', 'autopays', 'amortizationSent', 'paymentSchedules', 'nextAutopayPaymentDate']),
    ...mapState('settings', ['settings']),
    ...mapState('ticket', ['ticket']),
    ...mapState('settlement', ['pdsPaymentSchedule']),
    ...mapGetters('loanProLoan', ['paymentTypes', 'recurringAutopays', 'nextPaymentInfo', 'earlyAutopayPayments', 'nextPaymentDueDate']),

    loanDisplayId() {
      return this.ticket?.active_loan?.display_id ?? '';
    },

    loanId() {
      return this.ticket?.active_loan?.id ?? '';
    },

    routingNumber() {
      return this.paymentProfileSelected?.checking_account?.routing_number ?? '';
    },

    bankAccountTypeText() {
      return this.paymentProfileSelected?.type === 'paymentAccount.type.checking'
        ? 'Bank Account' : 'Debit Card';
    },

    customerEmail() {
      return this.customer?.email ?? '';
    },

    customerName() {
      return `${this.customer?.first_name} ${this.customer?.last_name}` ?? '';
    },

    recurringAutopayAmount() {
      return this.nextPaymentInfo?.paymentAmount ?? 0;
    },

    paymentsFrequency() {
      const paymentsFrequencies = {
        'loan.frequency.weekly': 'Weekly',
        'loan.frequency.biWeekly': 'Bi-Weekly',
        'loan.frequency.semiMonthly': 'Semi-Monthly',
        'loan.frequency.monthly': 'Monthly',
      };

      return paymentsFrequencies[this.activeLoan?.loan_setup?.payment_frequency] ?? '';
    },

    hasRecurringAutopay() {
      return this.recurringAutopays.length > 0;
    },

    hasEarlyPayment() {
      return this.earlyAutopayPayments.length > 0;
    },

    showNextDateNotice() {
      if (this.hasRecurringAutopay) {
        return false;
      }

      if (!this.nextPaymentDueDate || !this.nextAutopayPaymentDate?.paymentTime) {
        return false;
      }

      const paymentDate = DateTime.fromFormat(this.nextPaymentDueDate, 'yyyy-MM-dd').startOf('day');
      const autopayDate = DateTime.fromFormat(this.nextAutopayPaymentDate.dateLimit, 'yyyy-MM-dd').startOf('day');

      // eslint-disable-next-line max-len
      return autopayDate.valueOf() >= paymentDate.valueOf();
    },

    firstPaymentDate() {
      if (this.hasRecurringAutopay) {
        return this.nextPaymentInfo?.paymentDate ?? '';
      }

      return this.nextAutopayPaymentDate?.paymentTime ?? '';
    },

    totalSteps() {
      const { show_new_eft_document: showNewEftDocument } = this.settings?.brand ?? {};

      if (showNewEftDocument) {
        return 4;
      }

      return 2;
    },

    paymentProfileErrors() {
      const errors = [];

      if (!this.v$.paymentProfile.$dirty) {
        return errors;
      }
      if (!this.v$.paymentProfile.required) {
        errors.push('Payment method is required.');
      }
      return errors;
    },

    paymentProfileSelected() {
      return this.paymentProfiles.find((p) => p.id === this.paymentProfile) ?? null;
    },

    accountLast4() {
      return this.paymentProfileSelected?.title?.slice(-4) ?? '';
    },

    finalPaymentDate() {
      if (!this.activeLoan) {
        return null;
      }

      return this.activeLoan?.current_status?.final_payment_date ?? null;
    },

  },

  methods: {
    ...mapActions('autopays', ['updateAutopayPayment', 'createLoanRecurringAutopay', 'updateSelectedAutopayPayment']),
    ...mapActions('notification', ['setNotification']),
    ...mapActions('loanProLoan', ['getAutopays', 'sendAmortization']),

    handleCloseClickOutside() {
      this.resetData();
    },

    resetData() {
      this.enableRecurringAutopayModal = false;
      this.step = 1;
      this.paymentProfile = null;
      this.electronicDisclosureConsent = false;
      this.agreeFederalRegulations = false;
      this.loading = false;

      this.v$.$reset();
    },

    formatDate(dateString) {
      return DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss').toFormat('MM/dd/yyyy');
    },

    convertDateFormat(date) {
      if (!date) return null;

      let parsedDate = DateTime.fromFormat(date, 'yyyy-MM-dd HH:mm:ss');

      if (!parsedDate.isValid) {
        // Try the second format if the first one fails
        parsedDate = DateTime.fromFormat(date, 'yyyy-MM-dd');
      }

      return parsedDate.isValid ? parsedDate.toFormat('MM/dd/yyyy') : null;
    },

    nextStep() {
      if (this.step < this.totalSteps) {
        if (this.validateAutopay()) {
          this.step += 1;
        }
      } else {
        this.enableRecurringPayment();
      }
    },

    validateAutopay() {
      this.v$.$touch();

      if (this.step === 1) {
        return !this.v$.$invalid;
      }

      if (this.step === 2) {
        return this.electronicDisclosureConsent;
      }

      if (this.step === 3) {
        return this.agreeFederalRegulations;
      }

      return false;
    },
    async enableRecurringPayment() {
      this.loading = true;
      let response;

      // if there are existing recurring autopays, update their payment method
      if (this.recurringAutopays.length > 0) {
        const paymentProfile = this.paymentProfiles.find((pp) => pp.id === this.paymentProfile);
        response = await this.updateSelectedAutopayPayment({
          loanId: this.activeLoan.id,
          selectedAccount: paymentProfile,
          autopays: this.recurringAutopays,
        });
      } else {
        response = await this.createLoanRecurringAutopay({
          loanId: this.activeLoan.id,
          paymentAccountId: this.paymentProfile,
        });
      }

      await this.sendAmortization({
        ticketId: this.$route.params.id,
        name: this.customerName,
        bankAccountType: this.bankAccountTypeText,
        accountLast4: this.accountLast4,
        loanId: this.loanId,
        loanDisplayId: this.loanDisplayId,
        routingNumber: this.routingNumber,
        firstPaymentDate: this.convertDateFormat(this.firstPaymentDate),
      });

      this.loading = false;

      // get autopays
      await this.getAutopays();

      if (response && response.data === true) {
        await this.setNotification({
          color: 'green',
          message: 'Recurring Autopay has been enabled for selected payment method',
        });
      } else {
        await this.setNotification({
          color: 'red',
          message: 'Recurring Autopay cannot be enabled for selected payment method',
        });
      }

      this.resetData();
    },
  },
};
</script>

<style scoped>
.talk__off-one_title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #000000;
  line-height: 1.5rem;
}

.v-input--selection-controls {
  margin-top: 0;
}
</style>
